export const AUDIT_LOG_COLLECTION = 'audit_logs';
export const LEGAL_CONSENT_STORAGE_KEY = 'yugui_legal_consent';
/** 依法最低留存 180 天；常规日志实际留存 365 天（见 server/log-retention.js） */
export const AUDIT_LOG_MIN_RETENTION_DAYS = 180;
export const AUDIT_LOG_ROUTINE_RETENTION_DAYS = 365;
/** CloudBase audit_logs 集合：平台不作少于六个月的主动删除（云库文档默认长期保存） */
export const CLOUDBASE_AUDIT_RETENTION_DAYS = 180;
export const PRIVACY_POLICY_VERSION = '1.7';
export const USER_AGREEMENT_VERSION = '1.7';

export const AUDIT_ACTIONS = {
  LOGIN: 'login',
  REGISTER: 'register',
  PUBLISH: 'publish',
  DELETE: 'delete',
  ADMIN_TAKEDOWN: 'admin_takedown',
  ADMIN_RESTORE: 'admin_restore',
  ADMIN_BAN: 'admin_ban',
  ADMIN_UNBAN: 'admin_unban',
  ADMIN_BAN_REQUEST: 'admin_ban_request',
  ADMIN_BAN_APPROVE: 'admin_ban_approve',
  ADMIN_BAN_REJECT: 'admin_ban_reject',
  ADMIN_IGNORE_REPORT: 'admin_ignore_report',
  ADMIN_REVOKE_BAN: 'admin_revoke_ban',
  ADMIN_REVOKE_TAKEDOWN: 'admin_revoke_takedown',
  ADMIN_GRANT_ROLE: 'admin_grant_role',
  ADMIN_REVOKE_ROLE: 'admin_revoke_role',
  ADMIN_MODIFY_BAN: 'admin_modify_ban',
  REPORT: 'report',
  COMMENT: 'comment',
  ADOPT_MESSAGE: 'adopt_message',
  CONVENTION_ACCEPT: 'convention_accept',
  SITE_MAINTENANCE_ON: 'site_maintenance_on',
  SITE_MAINTENANCE_OFF: 'site_maintenance_off',
  SITE_MAINTENANCE_UPDATE: 'site_maintenance_update',
  ADMIN_WITHDRAW_ANN: 'admin_withdraw_ann',
  CONTENT_BLOCKED: 'content_blocked',
  LEGAL_CONSENT: 'legal_consent'
};

export const AUDIT_ACTION_LABELS = {
  login: '用户登录',
  register: '用户注册',
  publish: '发布内容',
  delete: '删除内容',
  admin_takedown: '管理员下架',
  admin_restore: '管理员恢复',
  admin_ban: '封禁账号',
  admin_unban: '解封账号',
  admin_ban_request: '申请封禁',
  admin_ban_approve: '批准封禁',
  admin_ban_reject: '驳回封禁',
  admin_ignore_report: '忽略举报',
  admin_revoke_ban: '超级管理员撤销封禁',
  admin_revoke_takedown: '超级管理员恢复作品',
  admin_grant_role: '授予管理员',
  admin_revoke_role: '取消管理员',
  admin_modify_ban: '调整封禁时长',
  report: '提交举报',
  comment: '发表评论',
  adopt_message: '领养私信',
  convention_accept: '接受平台公约',
  site_maintenance_on: '开启网站维护',
  site_maintenance_off: '关闭网站维护',
  site_maintenance_update: '更新维护说明',
  admin_withdraw_ann: '撤回全站公告',
  content_blocked: '拦截违规内容',
  legal_consent: '确认法律文件'
};

export const getAuditActionLabel = (action) =>
  AUDIT_ACTION_LABELS[action] || action || '未知操作';

export const ADMIN_MODERATION_ACTIONS = new Set([
  'admin_ban',
  'admin_unban',
  'admin_ban_request',
  'admin_ban_approve',
  'admin_ban_reject',
  'admin_ignore_report',
  'admin_takedown',
  'admin_restore',
  'admin_revoke_ban',
  'admin_revoke_takedown',
  'admin_grant_role',
  'admin_revoke_role',
  'admin_modify_ban'
]);

export const isAdminModerationLog = (log) =>
  ADMIN_MODERATION_ACTIONS.has(log?.action);

export const formatAuditBanLabel = (meta) => {
  if (!meta) return '';
  const days = meta.banDays;
  if (days === -1) return '永久封禁';
  if (days > 0) return `封禁 ${days} 天`;
  if (meta.banUntil) {
    return `封禁至 ${new Date(meta.banUntil).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
  }
  return '';
};

export const formatAdminGrantLabel = (meta) => {
  if (!meta) return '';
  if (meta.adminDays === -1) return '永久管理员（直至收回）';
  if (meta.adminDays > 0) return `临时管理员 ${meta.adminDays} 天`;
  if (meta.adminUntil) {
    return `管理员至 ${new Date(meta.adminUntil).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric' })}`;
  }
  return '管理员';
};

export const isContentDeleted = (item) =>
  !!(item?.deletedAt || item?.status === 'deleted' || item?.archived === true);

export const filterActiveFeedItems = (items) =>
  (Array.isArray(items) ? items : []).filter((item) => !isContentDeleted(item));

export const buildContentSnapshot = (post) => ({
  id: String(post?.id ?? post?._id ?? ''),
  type: post?.type || 'post',
  title: (post?.title || '').slice(0, 500),
  content: (post?.content || post?.description || '').slice(0, 2000),
  name: post?.name || '',
  petType: post?.petType || '',
  userId: post?.userId || '',
  user: post?.user || '',
  visibility: post?.visibility || 'public',
  createdAt: post?.createdAt || null,
  hasMedia: !!(post?.img || post?.isVideo)
});

const bufferAuditLogLocally = (logEntry) => {
  try {
    const key = 'yugui_audit_log_buffer';
    const buf = JSON.parse(localStorage.getItem(key) || '[]');
    buf.push(logEntry);
    localStorage.setItem(key, JSON.stringify(buf.slice(-300)));
  } catch (_) {
    /* ignore quota errors */
  }
};

const buildAuditApiUrl = () => {
  const base = (
    typeof process !== 'undefined' && process.env.REACT_APP_API_BASE
      ? String(process.env.REACT_APP_API_BASE).trim()
      : '/api'
  ).replace(/\/$/, '');
  if (base.startsWith('http')) return `${base}/audit/log`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    const b = base.startsWith('/') ? base : `/${base}`;
    return `${window.location.origin}${b}/audit/log`;
  }
  return `${base}/audit/log`;
};

/** 同步审计日志至服务端 SQLite（网络安全法第二十三条：留存必要记录） */
const syncAuditLogToServer = (logEntry) => {
  if (typeof fetch !== 'function') return;
  fetch(buildAuditApiUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(logEntry)
  }).catch(() => {});
};

/** 写入操作审计日志（本地缓冲 + 云库 audit_logs 集合） */
export const writeAuditLog = async (db, ensureAuth, entry) => {
  const logEntry = {
    action: entry.action || 'unknown',
    actorId: String(entry.actorId || ''),
    actorPhone: String(entry.actorPhone || ''),
    actorName: String(entry.actorName || ''),
    targetType: entry.targetType || '',
    targetId: String(entry.targetId || ''),
    summary: (entry.summary || '').slice(0, 500),
    snapshot: entry.snapshot || null,
    meta: entry.meta || null,
    userAgentHint:
      typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 160) : '',
    createdAt: Date.now(),
    retainUntil: Date.now() + AUDIT_LOG_MIN_RETENTION_DAYS * 24 * 60 * 60 * 1000
  };

  bufferAuditLogLocally(logEntry);
  syncAuditLogToServer(logEntry);

  if (!db) return false;
  try {
    if (typeof ensureAuth === 'function') await ensureAuth();
    await db.collection(AUDIT_LOG_COLLECTION).add(logEntry);
    return true;
  } catch (error) {
    console.warn('audit_logs 写入失败（请在云开发控制台创建集合并配置写权限）:', error);
    return false;
  }
};

/** 前台删除改为软删除：停止公开展示，后台保留必要记录以供核查 */
export const softDeleteContentInCloud = async (db, ensureAuth, collection, docId, deletedBy) => {
  const patch = {
    deletedAt: Date.now(),
    deletedBy: String(deletedBy || ''),
    status: 'deleted',
    archived: true,
    updatedAt: Date.now()
  };

  if (!db || !docId) return { success: false, patch };

  try {
    if (typeof ensureAuth === 'function') await ensureAuth();
    await db.collection(collection).doc(String(docId)).update(patch);
    return { success: true, patch };
  } catch (error) {
    console.warn('软删除失败:', error);
    return { success: false, patch, error };
  }
};

/** 管理员恢复已下架内容 */
export const restoreContentInCloud = async (db, ensureAuth, collection, docId, restoredBy) => {
  const patch = {
    deletedAt: null,
    deletedBy: '',
    status: 'active',
    archived: false,
    restoredAt: Date.now(),
    restoredBy: String(restoredBy || ''),
    updatedAt: Date.now()
  };

  if (!db || !docId) return { success: false, patch };

  try {
    if (typeof ensureAuth === 'function') await ensureAuth();
    await db.collection(collection).doc(String(docId)).update(patch);
    return { success: true, patch };
  } catch (error) {
    console.warn('恢复内容失败:', error);
    return { success: false, patch, error };
  }
};

export const saveLegalConsent = (phone, extra = {}) => {
  if (!phone) return;
  localStorage.setItem(
    LEGAL_CONSENT_STORAGE_KEY,
    JSON.stringify({
      phone: String(phone),
      privacyVersion: PRIVACY_POLICY_VERSION,
      agreementVersion: USER_AGREEMENT_VERSION,
      conventionVersion: extra.conventionVersion || null,
      acceptedAt: Date.now(),
      ...extra
    })
  );
};

export const getLegalConsent = (phone) => {
  if (!phone) return null;
  try {
    const raw = localStorage.getItem(LEGAL_CONSENT_STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : null;
    if (!data || String(data.phone) !== String(phone)) return null;
    return data;
  } catch {
    return null;
  }
};

/** 隐私政策或用户协议版本更新后须重新确认（网络安全法第二十三条） */
export const needsLegalConsentRenewal = (phone) => {
  const consent = getLegalConsent(phone);
  if (!consent) return true;
  return (
    consent.privacyVersion !== PRIVACY_POLICY_VERSION ||
    consent.agreementVersion !== USER_AGREEMENT_VERSION
  );
};
